/* Purelight — BREWLAB motion engine.
   reveal (+ wipe variant) · parallax · marquee · video autoplay guard.
   No dependencies, transform/opacity/clip-path only, reduced motion honoured,
   re-inits on shopify:section:load. */
(function () {
  'use strict';
  document.documentElement.classList.add('js');
  var RM = window.matchMedia('(prefers-reduced-motion: reduce)');
  function all(root, sel) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ---- reveal -----------------------------------------------------------
     Implements the measured recipe in ref2/ANIM-SPEC.md:
       - opacity + blur, never a transform
       - headings split PER CHARACTER, sweep normalised to ~1s per line
       - rows of siblings stagger RIGHT to LEFT (confirmed four times independently:
         tiles 63ms, flavour cards 70ms, blog cards 38ms, quote cards 158ms)
       - will-change is set on entry and dropped on transitionend, or a page with 200
         split characters keeps 200 compositor layers alive
  ------------------------------------------------------------------------ */
  var CHAR_SWEEP = 1000;   /* ms across a whole line, however many characters */

  /* Only headings get the per-character treatment. Splitting a PARAGRAPH into characters
     makes every character a soft-wrap opportunity, which broke words mid-word
     ("contain er", "anythi ng"). Words are wrapped in a nowrap box so the characters can
     animate individually while the line still breaks between words, and spaces stay as
     plain text nodes so a trailing space cannot claim a phantom line box of its own. */
  var CHAR_MAX = 64;
  function canSplit(el) {
    if (el.querySelector('a, button, input, img, svg')) return false;   /* never split interactive or media content */
    var txt = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!txt || txt.length > CHAR_MAX) return false;
    return /^(H[1-6])$/.test(el.tagName) ||
           /\bbl-h[123]\b/.test(el.className) ||
           /\bbl-(hero__line|close__line)\b/.test(el.className);
  }

  function splitChars(el) {
    if (el.dataset.blSplit === '1') return;
    el.dataset.blSplit = '1';
    var full = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (!full) return;
    if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', full);
    var cells = [];
    (function walk(node) {
      Array.prototype.slice.call(node.childNodes).forEach(function (n) {
        if (n.nodeType === 3) {
          var txt = n.nodeValue;
          if (!txt) return;
          var frag = document.createDocumentFragment();
          txt.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
            var w = document.createElement('span');
            w.className = 'bl-w';
            w.setAttribute('aria-hidden', 'true');
            for (var i = 0; i < part.length; i++) {
              var s = document.createElement('span');
              s.className = 'bl-c';
              s.textContent = part[i];
              cells.push(s);
              w.appendChild(s);
            }
            frag.appendChild(w);
          });
          node.replaceChild(frag, n);
        } else if (n.nodeType === 1) {
          n.setAttribute('aria-hidden', 'true');
          walk(n);
        }
      });
    })(el);
    var n = cells.length;
    cells.forEach(function (s, i) {
      s.style.setProperty('--bl-delay', (n > 1 ? Math.round(i / (n - 1) * CHAR_SWEEP) : 0) + 'ms');
    });
  }

  function dropWillChange(el) {
    el.addEventListener('transitionend', function h() {
      el.style.willChange = '';
      el.removeEventListener('transitionend', h);
    });
  }

  /* Assign --i so that siblings sharing a row animate right-to-left, while anything
     alone on its row keeps document order. Rows are detected by rounded offsetTop. */
  function orderRows(nodes) {
    var rows = {};
    nodes.forEach(function (el, idx) {
      var r = el.getBoundingClientRect();
      var key = Math.round((r.top + window.scrollY) / 24);
      (rows[key] || (rows[key] = [])).push({ el: el, x: r.left, idx: idx });
    });
    var out = [];
    Object.keys(rows).sort(function (a, b) { return a - b; }).forEach(function (k) {
      var row = rows[k];
      if (row.length > 1) row.sort(function (a, b) { return b.x - a.x; });   /* right → left */
      else row.sort(function (a, b) { return a.idx - b.idx; });
      row.forEach(function (o) { out.push(o.el); });
    });
    return out;
  }

  function markIn(root) {
    var kids = orderRows(all(root, '.bl-r'));
    var step = parseInt(root.getAttribute('data-bl-stagger'), 10) || 70;
    kids.forEach(function (c, i) {
      c.style.setProperty('--i', i);
      if (c.classList.contains('bl-r--wipe') && canSplit(c)) {
        splitChars(c);
        Array.prototype.forEach.call(c.querySelectorAll('.bl-c'), function (s) {
          var base = parseInt(s.style.getPropertyValue('--bl-delay'), 10) || 0;
          s.style.setProperty('--bl-delay', (base + i * step) + 'ms');
          s.style.willChange = 'opacity, filter';
          dropWillChange(s);
        });
      } else {
        c.classList.remove('bl-r--wipe');           /* falls back to the block fade */
        c.style.setProperty('--bl-delay', (i * step) + 'ms');
        c.style.willChange = 'opacity, filter';
        dropWillChange(c);
      }
      c.classList.add('is-in');
    });
    root.classList.add('is-in');
  }

  function reveal(root) {
    var nodes = all(root, '[data-bl-reveal]');
    if (!nodes.length) return;
    if (RM.matches || !('IntersectionObserver' in window)) { nodes.forEach(markIn); return; }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        markIn(e.target);
        io.unobserve(e.target);
      });
    }, { threshold: 0.2, rootMargin: '0px 0px -8% 0px' });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* A dedicated observer for the pour. It is not always inside a section's reveal root —
     bl-benefits puts `data-bl-reveal` on an element that comes AFTER the media in the DOM —
     and a reveal that silently never fires is exactly the class of bug this engine has
     already cost us once. Observing the elements themselves removes the dependency. */
  function pours(root) {
    var nodes = all(root, '.bl-r--pour');
    if (!nodes.length) return;
    if (RM.matches || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
    nodes.forEach(function (n) { io.observe(n); });
  }

  /* ---- parallax ------------------------------------------------------- */
  var px = [];
  function parallax(root) {
    all(root, '[data-bl-parallax]').forEach(function (n) { if (px.indexOf(n) === -1) px.push(n); });
  }
  function tickParallax() {
    if (RM.matches) return;
    var vh = window.innerHeight;
    for (var i = 0; i < px.length; i++) {
      var n = px[i], r = n.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) continue;
      var s = parseFloat(n.getAttribute('data-bl-parallax')) || 0.12;
      n.style.transform = 'translate3d(0,' + (-((r.top + r.height / 2 - vh / 2) * s)).toFixed(1) + 'px,0)';
    }
  }

  /* ---- tag filter chips (no app, no request) --------------------------- */
  function filters(root) {
    all(root, '[data-bl-filter]').forEach(function (sec) {
      if (sec.dataset.blFilter === 'on') return;
      sec.dataset.blFilter = 'on';
      var chips = all(sec, '[data-bl-chip]');
      var items = all(sec, '[data-bl-tags]');
      var empty = sec.querySelector('[data-bl-empty]');
      if (!chips.length) return;
      function apply(tag) {
        var shown = 0;
        items.forEach(function (it) {
          var tags = (it.getAttribute('data-bl-tags') || '').toLowerCase().split('|');
          var on = (tag === '*') || tags.indexOf(tag) !== -1;
          it.classList.toggle('is-hidden', !on);
          if (on) shown++;
        });
        chips.forEach(function (c) {
          var active = c.getAttribute('data-bl-chip') === tag;
          c.classList.toggle('is-on', active);
          c.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        if (empty) empty.style.display = shown ? 'none' : 'block';
      }
      chips.forEach(function (c) {
        c.addEventListener('click', function (e) {
          e.preventDefault(); apply(c.getAttribute('data-bl-chip'));
        });
      });
      /* A chip that matches nothing is a dead end — the product it filters for is outside the
         shown set. Hide it rather than letting someone click into an empty grid. */
      chips.forEach(function (c) {
        var tag = c.getAttribute('data-bl-chip');
        if (tag === '*') return;
        var hits = items.filter(function (it) {
          return (it.getAttribute('data-bl-tags') || '').toLowerCase().split('|').indexOf(tag) !== -1;
        }).length;
        if (!hits) c.hidden = true;
      });
      apply('*');
    });
  }

  /* ---- marquee -------------------------------------------------------- */
  function marquee(root) {
    all(root, '[data-bl-marquee]').forEach(function (t) {
      if (t.dataset.blDone) return; t.dataset.blDone = '1';
      var speed = parseFloat(t.getAttribute('data-bl-marquee')) || 40;
      var inner = t.firstElementChild; if (!inner) return;
      inner.appendChild(inner.cloneNode(true));
      t.style.setProperty('--bl-marquee-dur', (inner.scrollWidth / 2 / speed) + 's');
    });
  }

  /* ---- background video: only play what is on screen ------------------- */
  function videos(root) {
    var vids = all(root, 'video[data-bl-video]');
    if (!vids.length) return;
    vids.forEach(function (v) {
      v.muted = true; v.defaultMuted = true; v.playsInline = true; v.loop = true;
      if (RM.matches) { try { v.pause(); } catch (e) {} return; }
    });
    if (RM.matches || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
        else { try { v.pause(); } catch (err) {} }
      });
    }, { threshold: 0.05 });
    vids.forEach(function (v) { io.observe(v); });
  }

  var ticking = false;
  function onScroll() {
    if (ticking) return; ticking = true;
    requestAnimationFrame(function () { tickParallax(); ticking = false; });
  }
  function init(root) { reveal(root); pours(root); filters(root); parallax(root); marquee(root); videos(root); onScroll(); }
  function boot() {
    init(document);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
